import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  AlertDetailApiResponse,
  AlertJurisdictionResponse,
  AlertPreviewResponse,
  AlertRenderingStyleResponse,
  AlertsApiResponse,
  AlertTagsResponse,
  ExportAlertsExcelParameters,
  ExportAlertsExcelResponse,
  GetAlertsParameters,
  JurisdictionsResponse,
  ProductsResponse,
  ReportCommentsResponse,
  SaveAlertPayload,
  SaveAlertResponse,
} from '../types/allAlerts';
import type { GetSharedData, GetSharedRedirectResponse } from '../types/common';
import { v2ApiURL } from '../util/constants';
import { keyFactory } from '../util/keyFactory';
import { logger } from '../util/logger';
import { FileExportClient,HttpClient } from './axiosConfig';
type AlertId = number | string | null;

// @param categoryId - category ID (-1 to get all tags)
export const useGetAlertTags = (categoryId?: number | null) => {
  // default to -1 if categoryId is missing or null
  const effectiveCategoryId = categoryId ?? -1;

  return useQuery({
    queryKey: [keyFactory.getAlertTags, effectiveCategoryId],
    queryFn: async () => {
      const result = await HttpClient.get<AlertTagsResponse>(
        `/commons/tags?categoryId=${effectiveCategoryId}`
      );
      return result.data;
    },
    enabled: true,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// Non-hook function to get tags for a given category
export const getAlertTags = async (categoryId?: number | null) => {
  const effectiveCategoryId = categoryId ?? -1;
  const result = await HttpClient.get<AlertTagsResponse>(
    `/commons/tags?categoryId=${effectiveCategoryId}`
  );
  return result.data;
};

export const tagsService = {
  getAlertTags,
};

//  * Hook to get jurisdictions.
//  * @param productId - Product ID (-1 to get all jurisdictions)
export const useGetProductRelevantJurisdictions = (
  productIds?: Array<number> | number | null
) => {
  // Convert to array if single number, or use default if null/undefined
  let effectiveProductIds: Array<number>; // or appropriate type

  if (Array.isArray(productIds)) {
    effectiveProductIds = productIds;
  } else if (productIds === null || productIds === undefined) {
    effectiveProductIds = [-1];
  } else {
    effectiveProductIds = [productIds];
  }

  const productIdsString = effectiveProductIds.join(',');

  return useQuery({
    queryKey: [keyFactory.getAlertJurisdictions, productIdsString],
    queryFn: async () => {
      const result = await HttpClient.get<JurisdictionsResponse>(
        `/users/alerts/product-relevant-jurisdictions?renderingStyleId=${productIdsString}`
      );
      return result.data;
    },
    enabled: true,
    staleTime: 5 * 60 * 1000,
    gcTime: 0,
  });
};
//  * Hook to get jurisdictions.
//  * @param productId - Product ID (-1 to get all jurisdictions)
export const useGetJurisdictions = (
  productIds?: Array<number> | number | null
) => {
  // Convert to array if single number, or use default if null/undefined
  let effectiveProductIds: Array<number>; // or appropriate type

  if (Array.isArray(productIds)) {
    effectiveProductIds = productIds;
  } else if (productIds === null || productIds === undefined) {
    effectiveProductIds = [-1];
  } else {
    effectiveProductIds = [productIds];
  }

  const productIdsString = effectiveProductIds.join(',');

  return useQuery({
    queryKey: [keyFactory.getAlertJurisdictions, productIdsString],
    queryFn: async () => {
      const result = await HttpClient.get<JurisdictionsResponse>(
        `/users/products/alert-jurisdictions?productIds=${productIdsString}`
      );
      return result.data;
    },
    enabled: true,
    staleTime: 5 * 60 * 1000,
    gcTime: 0,
  });
};

//  * Non-hook function to get jurisdictions for a given product
export const getJurisdictions = async (
  productIds?: Array<number> | number | null
) => {
  let effectiveProductIds: Array<number>; // adjust type if needed

  if (Array.isArray(productIds)) {
    effectiveProductIds = productIds;
  } else if (productIds === null || productIds === undefined) {
    effectiveProductIds = [-1];
  } else {
    effectiveProductIds = [productIds];
  }

  const productIdsString = effectiveProductIds.join(',');
  const result = await HttpClient.get<JurisdictionsResponse>(
    `/users/products/alert-jurisdictions?productIds=${productIdsString}`
  );
  return result.data;
};

export const jurisdictionsService = {
  getJurisdictions,
};

export const useGetAlerts = (
  parameters?: GetAlertsParameters,
  options?: { enabled?: boolean; triggerKey?: number | string }
) => {
  // Set defaults and convert to string for URLSearchParams
  const {
    search = '',
    renderStyleIds = '-1',
    jurisdictionIds = '-1',
    statuses = '-1',
    categoryId = -1,
    alertTagIds = '-1',
    fromDate = '',
    toDate = '',
    pageNumber = 1,
    numberOfRecords = 50,
    sortBy = 'desc',
    orderBy = 'publicationDate',
    days = 0,
    type = 'alerts',
    isTopFive = false,
  } = parameters ?? {};

  const queryString = new URLSearchParams({
    search,
    renderStyleIds,
    jurisdictionIds,
    statuses,
    categoryId: categoryId.toString(),
    alertTagIds,
    fromDate,
    toDate,
    pageNumber: pageNumber.toString(),
    numberOfRecords: numberOfRecords.toString(),
    sortBy,
    orderBy,
    days: days.toString(),
    type,
    isTopFive: isTopFive.toString(),
  }).toString();

  return useQuery({
    // Only refetch when caller changes the triggerKey; decouple from params changes
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [keyFactory.getAllAlerts, options?.triggerKey ?? 'initial'],
    queryFn: async () => {
      const result = await HttpClient.get<AlertsApiResponse>(
        `${v2ApiURL}/users/alerts?${queryString}`
      );
      return result.data;
    },
    enabled: options?.enabled ?? true,
    staleTime: 0,
    gcTime: 0,
  });
};

export const getAlerts = async (
  parameters?: GetAlertsParameters
): Promise<AlertsApiResponse> => {
  const {
    renderStyleIds = '-1',
    jurisdictionIds = '-1',
    statuses = '-1',
    categoryId = -1,
    alertTagIds = '-1',
    fromDate = '',
    toDate = '',
    pageNumber = 1,
    numberOfRecords = 50,
    sortBy = 'desc',
    orderBy = 'publicationDate',
    days = 0,
    type = 'alerts',
    isTopFive = false,
  } = parameters ?? {};

  const queryString = new URLSearchParams({
    renderStyleIds,
    jurisdictionIds,
    statuses,
    categoryId: categoryId.toString(),
    alertTagIds,
    fromDate,
    toDate,
    pageNumber: pageNumber.toString(),
    numberOfRecords: numberOfRecords.toString(),
    sortBy,
    orderBy,
    days: days.toString(),
    type,
    isTopFive: isTopFive.toString(),
  }).toString();

  const result = await HttpClient.get<AlertsApiResponse>(
    `${v2ApiURL}/users/alerts?${queryString}`
  );
  return result.data;
};

export const alertsService = {
  getAlerts,
};

// Hook to fetch alert details (comments) by alertId
export const useGetAlertDetail = (alertId: number | null) => {
  return useQuery({
    queryKey: ['alertDetail', alertId],
    queryFn: async () => {
      if (!alertId) throw new Error('Alert ID is required');
      const result = await HttpClient.get<AlertDetailApiResponse>(
        `/users/alerts/${alertId}/comments`
      );
      return result.data;
    },
    enabled: !!alertId,
    staleTime: 0,
    gcTime: 0,
  });
};

// Non-hook function to get alert detail by ID
export const getAlertDetail = async (
  alertId: number
): Promise<AlertDetailApiResponse> => {
  const result = await HttpClient.get<AlertDetailApiResponse>(
    `/users/alerts/${alertId}/comments`
  );
  return result.data;
};

export const alertDetailService = {
  getAlertDetail,
};

export const useGetAlertSummary = (alertId: number | null) => {
  return useQuery({
    queryKey: ['alertSummary', alertId],
    queryFn: async () => {
      if (!alertId) {
        throw new Error('Alert ID is required');
      }
      const result = await HttpClient.get<AlertDetailApiResponse>(
        '/users/alerts/alert-details',
        {
          params: { alertId },
        }
      );
      return result.data;
    },
    enabled: !!alertId,
  });
};

export const getAlertDocumentBlob = async (apiUrl: string) => {
  return HttpClient.get<Blob>(apiUrl, { responseType: 'blob' });
};

/**
 * Same view-ck-editor-doc endpoint with json=true — returns redirect/PDF-viewer metadata
 * (fileName, filePermission, redirectEnum) instead of a file blob.
 */
export const getAlertViewCkEditorDocumentMeta = async (
  queryString: string
): Promise<GetSharedData | null> => {
  const url = `storage/view-ck-editor-doc?json=true&${queryString}`;
  const result = await HttpClient.get<GetSharedRedirectResponse>(url);
  return result.data?.data ?? null;
};

const appendDynamicFormFields = (
  formData: FormData,
  dynamicFormFields: Record<string, string | number> | undefined
) => {
  if (!dynamicFormFields) {
    return;
  }

  Object.entries(dynamicFormFields).forEach(([key, value]) => {
    formData.append(`dynamicFormFields[${key}]`, String(value));
  });
};


const buildSaveAlertFormData = (payload: SaveAlertPayload) => {
  const formData = new FormData();

  const productIds = payload.productIds ?? [];
  const jurisdictionIds = payload.jurisdictionIds ?? [];
  const tagIds = payload.tagIds ?? [];

  const uniqueProducts = [...new Set(productIds)].join(',');
  const uniqueJurisdictions = [...new Set(jurisdictionIds)].join(',');
  const tagValue = tagIds.length > 0 ? [...new Set(tagIds)].join(',') : '0';

  const publicationDateValue = payload.publicationDate ?? '';

  const uploadAttachmentFlag = payload.newAttachments.length > 0 ? '1' : '0';

  const categoryIdValue = payload.categoryId ?? -1;
  const categoryTypeValue = payload.categoryType ?? -1;
  formData.append('categoryId', String(categoryIdValue));
  formData.append('categoryType', String(categoryTypeValue));

  formData.append('uploadAttachment', uploadAttachmentFlag);

  const alertIdValue = payload.alertId ?? 0;
  formData.append('alertId', String(alertIdValue));

  formData.append('edit', payload.isEditMode ? '1' : '0');
  formData.append('preview', payload.preview ? '1' : '0');
  formData.append('save', payload.action);

  formData.append('chkAlwaysShowOnReport', payload.alwaysShowOnReport ? '1' : '0');
  formData.append('hideAlert', payload.hideAlertOnReport ? '1' : '0');
  formData.append('tagSelNode', tagValue);
  formData.append('pageType', payload.pageType);

  const sourceValue = uniqueProducts ?? payload.source ?? '';
  if (sourceValue) {
    formData.append('source', sourceValue);
  }
  if (payload.renderStyle) {
    formData.append('renderStyle', payload.renderStyle);
  }
  if (payload.searchJurisdiction !== undefined && payload.searchJurisdiction !== null) {
    formData.append('searchJurisdiction', payload.searchJurisdiction);
  }
  formData.append('chkProduct', uniqueProducts);
  formData.append('chkJurisdiction', uniqueJurisdictions);
  formData.append('impact', String(payload.impactId ?? -1));
  formData.append('publicationDate', publicationDateValue);

  formData.append('fckEditorTitle', payload.title ?? '');
  formData.append('fckEditorSummary', payload.summary ?? '');

  payload.newAttachments.forEach((attachment) => {
    formData.append('alertDocTitle', attachment.fileName);
    formData.append('alertDocFileName', attachment.file, attachment.fileName);
  });

  appendDynamicFormFields(formData, payload.dynamicFormFields);

  return formData;
};

export const useSaveAlertMutation = () => {
  return useMutation<SaveAlertResponse, unknown, SaveAlertPayload>({
    mutationFn: async (payload: SaveAlertPayload) => {
      const formData = buildSaveAlertFormData(payload);
      const result = await HttpClient.post<SaveAlertResponse>(
        '/users/alerts/alerts',
        formData
      );
      return result.data;
    },
  });
};

export const useGetAlertPreview = (
  alertId: AlertId,
  options?: { enabled?: boolean }
) => {
  const enabled = options?.enabled ?? Boolean(alertId);
  return useQuery<AlertPreviewResponse>({
    queryKey: [keyFactory.getAlertPreview, alertId],
    queryFn: async () => {
      if (alertId === null || alertId === undefined || alertId === '') {
        throw new Error('Alert ID is required to fetch preview data.');
      }
      const result = await HttpClient.get<AlertPreviewResponse>(
        '/users/alerts/window-data',
        {
          params: { alertId },
        }
      );
      return result.data;
    },
    enabled,
  });
};

//use to get the products
export const useGetProducts = (
  categoryId: number = -1,
  allProductId: number = 0
) => {
  return useQuery({
    queryKey: [keyFactory.getProducts, categoryId, allProductId],
    queryFn: async () => {
      const result = await HttpClient.get<ProductsResponse>(
        `/users/products?categoryId=${categoryId}&allProductId=${allProductId}`
      );
      return result.data;
    },
    enabled: true,
    staleTime: 5 * 60 * 1000,
    gcTime: 0,
  });
};

//products
export const getProducts = async (
  categoryId: number = -1,
  allProductId: number = 0
): Promise<ProductsResponse> => {
  const result = await HttpClient.get<ProductsResponse>(
    `/users/products?categoryId=${categoryId}&allProductId=${allProductId}`
  );
  return result.data;
};

export const productsService = {
  getProducts,
};

// rendering style
export const useGetAlertRenderingStyle = (
  productIds: Array<number> | number,
  categoryIds: Array<number> | number
) => {
  const productIdsString = Array.isArray(productIds)
    ? productIds.join(',')
    : productIds.toString();

  const categoryIdsString = Array.isArray(categoryIds)
    ? categoryIds.join(',')
    : categoryIds.toString();

  return useQuery({
    queryKey: [
      keyFactory.getAlertRenderingStyle,
      productIdsString,
      categoryIdsString,
    ],
    queryFn: async () => {
      const result = await HttpClient.get<AlertRenderingStyleResponse>(
        `/users/products/alert-renderingStyle?productIds=${productIdsString}&categoryId=${categoryIdsString}`
      );
      return result.data;
    },
    enabled: productIdsString.length > 0 && categoryIdsString.length > 0,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const getAlertRenderingStyle = async (
  productIds: Array<number> | number,
  categoryIds: Array<number> | number
): Promise<AlertRenderingStyleResponse> => {
  const productIdsString = Array.isArray(productIds)
    ? productIds.join(',')
    : productIds.toString();

  const categoryIdsString = Array.isArray(categoryIds)
    ? categoryIds.join(',')
    : categoryIds.toString();

  const result = await HttpClient.get<AlertRenderingStyleResponse>(
    `/users/products/alert-renderingStyle?productId=${productIdsString}&categoryIds=${categoryIdsString}`
  );
  return result.data;
};

export const alertRenderingStyleService = {
  getAlertRenderingStyle,
};

// Product banner service
export interface ProductBannerData {
  renderStyleId?: number;
  renderStyleID?: number;
  renderStyleSmallLogo?: string;
  renderStyleLargeBanner?: string;
  imagePath?: string;
  [key: string]: unknown;
}

export interface ProductBannerResponse {
  message?: string;
  data?: Array<ProductBannerData>;
}

// Non-hook function to get product banner
export const getProductBanner = async (
  renderStyleId: number
): Promise<ProductBannerResponse | null> => {
  try {
    const result = await HttpClient.get<ProductBannerResponse>(
      `/users/products/banners?renderStyleId=${renderStyleId}`
    );
    return result.data;
  } catch (error) {
    logger.error('Failed to fetch product banner', error);
    return null;
  }
};

// Hook to get product banner using TanStack Query
export const useGetProductBanner = (
  renderStyleId: number | null | undefined,
  options?: { enabled?: boolean }
) => {
  const enabled = options?.enabled ?? Boolean(renderStyleId);
  
  return useQuery({
    queryKey: [keyFactory.getProductBanner, renderStyleId],
    queryFn: async () => {
      if (!renderStyleId) {
        throw new Error('Render style ID is required to fetch product banner.');
      }
      const result = await getProductBanner(renderStyleId);
      return result;
    },
    enabled,
  });
};
// report service
export const useGetReportComments = (
  reportIds: Array<number> | number,
  search: string = ''
) => {
  const reportIdsString = Array.isArray(reportIds)
    ? reportIds.join(',')
    : reportIds.toString();

  return useQuery({
    queryKey: [keyFactory.getReportComments, reportIdsString, search],
    queryFn: async () => {
      const result = await HttpClient.get<ReportCommentsResponse>(
        `/users/reports/comment?reportIds=${reportIdsString}&serach=${encodeURIComponent(search)}`
      );
      return result.data;
    },
    enabled: !!reportIdsString && reportIdsString !== '0', // won't run if empty
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const getReportComments = async (
  reportIds: Array<number> | number,
  search: string = ''
): Promise<ReportCommentsResponse> => {
  const reportIdsString = Array.isArray(reportIds)
    ? reportIds.join(',')
    : reportIds.toString();

  const result = await HttpClient.get<ReportCommentsResponse>(
    `/users/reports/comment?reportIds=${reportIdsString}&serach=${encodeURIComponent(search)}`
  );
  return result.data;
};

export const reportCommentsService = {
  getReportComments,
};

interface DeleteAlertAttachmentParameters {
  ealertId: string | number;
  edocId: string | number;
}

export const useDeleteAlertAttachment = () => {
  return useMutation({
    mutationFn: async ({ ealertId, edocId }: DeleteAlertAttachmentParameters) => {
      const response = await HttpClient.delete(`/users/alerts/attachments`, {
        params: {
          ealertId,
          edocId,
        },
      });
      return response.data;
    },
  });
};

interface AttachPreviousAlertsPayload {
  mainAlertId: string | number;
  attachedAlertIds: Array<string | number>;
}

export const useAttachPreviousAlertsMutation = () => {
  return useMutation({
    mutationFn: async ({ mainAlertId, attachedAlertIds }: AttachPreviousAlertsPayload) => {
      const normalizedAttachedAlertIds = attachedAlertIds
        .map((value) => {
          if (value === null || value === undefined) return '';
          return value.toString().trim();
        })
        .filter((value) => value.length > 0);

      const response = await HttpClient.post(
        `/users/alerts/attachments`,
        null,
        {
          params: {
            mainAlertId,
            attachedAlertIds: normalizedAttachedAlertIds.join(','),
          },
        }
      );
      return response.data;
    },
  });
};

interface DeleteAlertParameters {
  alertId?: number | string | null;
  encryptAlertId?: string | number | null;
  eurl?: string | null;
}

export const useDeleteAlertMutation = () => {
  return useMutation({
    mutationFn: async ({ alertId }: DeleteAlertParameters) => {
      const response = await HttpClient.delete(`/users/alerts/delete?alertId=${alertId}`);
      return response.data;
    },
  });
};

export const useGetAlertJurisdiction = (
  alertId: number | null,
  jurisdictionId: number | null,
  options?: { enabled?: boolean }
) => {
  const enabled =
    options?.enabled ?? (Boolean(alertId) && Boolean(jurisdictionId));

  return useQuery<AlertJurisdictionResponse>({
    queryKey: [keyFactory.getAlertJurisdictionCheck, alertId, jurisdictionId],
    queryFn: async () => {
      if (!alertId || !jurisdictionId) {
        throw new Error('Alert ID and Jurisdiction ID are required.');
      }
      const result = await HttpClient.get<AlertJurisdictionResponse>(
        `/users/alerts/${alertId}/jurisdictions/${jurisdictionId}`
      );
      return result.data;
    },
    enabled,
  });
};

export const useExportAlertsExcel = () => {
  return useMutation({
    mutationFn: async (parameters: ExportAlertsExcelParameters) => {
      const {
        search = '',
        renderStyleIds = '-1',
        jurisdictionIds = '-1',
        statuses = '-1',
        alertTagIds = '-1',
        fromDate = '',
        toDate = '',
        pageNumber = 1,
        numberOfRecords = -1,
        sortby = 'desc',
        orderby = 'publicationDate',
        showReportsUpdate = 0,
        days = 365,
      } = parameters;

      const queryParameters = new URLSearchParams({
        search,
        renderStyleIds,
        jurisdictionIds,
        statuses,
        alertTagIds,
        fromDate,
        toDate,
        pageNumber: pageNumber.toString(),
        numberOfRecords: numberOfRecords.toString(),
        sortby,
        orderby,
        showReportsUpdate: showReportsUpdate.toString(),
        days: days.toString(),
      });

      const response = await FileExportClient.get<ExportAlertsExcelResponse>(
        `/users/alerts/excel/generate?${queryParameters.toString()}`
      );

      return response?.data;
    },
  });
};